<template>
  <div class="locale-picker">
    <div class="dropdown" :class="{ 'is-active': isOpen }">
      <div class="dropdown-trigger">
        <button
          class="button is-small is-primary"
          aria-haspopup="true"
          :aria-controls="`locale-dropdown-${componentId}`"
          @click="toggleDropdown"
          :title="t('localePicker.title')"
          data-testid="locale-picker-button"
        >
          <span class="locale-label">{{ t('localePicker.label') }}</span>
          <span class="icon is-small">
            <i class="fas fa-angle-down" aria-hidden="true"></i>
          </span>
        </button>
      </div>
      <div
        class="dropdown-menu"
        :id="`locale-dropdown-${componentId}`"
        role="menu"
      >
        <div class="dropdown-content">
          <a
            v-for="locale in availableLocales"
            :key="locale.code"
            class="dropdown-item"
            :class="{ 'is-active': locale.code === currentLocale }"
            @click="selectLocale(locale.code)"
            :data-testid="`locale-option-${locale.code}`"
          >
            <span class="locale-name">{{ locale.name }}</span>
            <span v-if="locale.code === currentLocale" class="icon is-small">
              <i class="fas fa-check" aria-hidden="true"></i>
            </span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { saveLocalePreference, type SupportedLocale } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

const { locale, t } = useI18n()
const authStore = useAuthStore()
const isOpen = ref(false)
const componentId = Math.random().toString(36).substring(2, 9)

interface LocaleOption {
  code: SupportedLocale
  name: string
}

const availableLocales: LocaleOption[] = [
  { code: 'en', name: 'English' },
  { code: 'cs', name: 'Čeština' },
]

const currentLocale = computed(() => locale.value as SupportedLocale)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const closeDropdown = () => {
  isOpen.value = false
}

const selectLocale = async (newLocale: SupportedLocale) => {
  if (newLocale === currentLocale.value) {
    closeDropdown()
    return
  }

  // Update i18n locale immediately
  locale.value = newLocale

  // Save to localStorage for all users
  saveLocalePreference(newLocale)

  // If logged in, also save to server
  if (authStore.isAuthenticated) {
    try {
      const response = await fetch('/api/users/me/locale', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ locale: newLocale }),
      })

      if (response.ok) {
        // Update user locale in auth store
        authStore.updateUser({ locale: newLocale })
      } else {
        console.warn('Failed to save locale to server')
      }
    } catch (error) {
      console.error('Error saving locale to server:', error)
    }
  }

  closeDropdown()
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const dropdown = document.querySelector(
    `#locale-dropdown-${componentId}`
  )?.parentElement
  if (dropdown && !dropdown.contains(event.target as Node)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.locale-picker {
  display: inline-block;
}

.locale-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.dropdown-item .locale-name {
  flex: 1;
}
</style>
